import React, { useContext } from 'react';
import type { Styling } from 'src/context/styling';
import type { CSSInterpolation } from '@emotion/serialize';
import { SiteContext } from 'src/context/site-context';
import { flex, shadow } from 'src/styling/constants';
import { mqReducedMotion } from 'src/styling/media-queries';
import type {
  HeaderButtonLinkProps,
  HeaderButtonProps,
} from 'src/components/layout/redesign/header-mobile/header-button';
import HeaderButton from 'src/components/layout/redesign/header-mobile/header-button';

const createComponentStyling = (styling: Styling) =>
  ({
    wrapper: {
      'position': 'relative',
      'padding': '1.25rem',
      'gap': '1rem',
      'backgroundColor': styling.colors.cream,
      'alignItems': 'center',
      '& a': {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '30rem',
      },
    },
    button: {
      justifyContent: 'center',
      width: '100%',
      maxWidth: '30rem',
    },
  }) satisfies Record<string, CSSInterpolation>;

const createAnimationStyles = (isMenuOpen: boolean): CSSInterpolation =>
  mqReducedMotion({
    transition: ['top 0.4s ease-in-out', 'none'],
    top: [isMenuOpen ? 0 : '100%', 0],
  });

type HeaderMobileBottomNavProps = {
  isMenuOpen: boolean;
  primaryButton?: HeaderButtonProps;
  secondaryButton?: HeaderButtonProps;
  linkDropdownCta?: Queries.LinkDropdownCta;
};

const HeaderMobileBottomNav = ({
  isMenuOpen,
  primaryButton,
  secondaryButton,
  linkDropdownCta,
}: HeaderMobileBottomNavProps) => {
  const { styling } = useContext(SiteContext);
  const componentStyling = createComponentStyling(styling);
  const showMobilePrimaryMenuButton =
    linkDropdownCta?.enabled &&
    linkDropdownCta?.mobilePrimaryMenuButton?.displayText &&
    linkDropdownCta?.mobilePrimaryMenuButton?.link;

  return (
    <div
      css={[componentStyling.wrapper, flex.column, shadow.above, createAnimationStyles(isMenuOpen)]}
    >
      {primaryButton &&
        (showMobilePrimaryMenuButton ? (
          <HeaderButton
            {...primaryButton}
            buttonProps={{
              ...(primaryButton as HeaderButtonLinkProps).buttonProps,
              buttonLink: linkDropdownCta?.mobilePrimaryMenuButton.link,
              buttonText: linkDropdownCta?.mobilePrimaryMenuButton.displayText,
            }}
            type="link"
            styles={componentStyling.button}
          />
        ) : (
          <HeaderButton {...primaryButton} styles={componentStyling.button} />
        ))}
      {secondaryButton && <HeaderButton {...secondaryButton} styles={componentStyling.button} />}
    </div>
  );
};

export default HeaderMobileBottomNav;
